import { Link } from "gatsby";
import {
    Calculator,
    CheckCircle2,
    ExternalLink,
    FileText,
    Trophy,
} from "lucide-react";
import React, { useState } from "react";
import Layout from "../../components/layouts/Layout";
import HeroSection from "../../components/newVersion/HeroSection";
import { cn } from "../../utils/cn";
import {
    calculateSubtest,
    MOSSOS_RULES,
    type Answers,
    type SubtestRule,
} from "../../utils/mossosCalculator";

const OFFICIAL_URL =
    "https://mossos.gencat.cat/ca/els_mossos_desquadra/acces_al_cos/Mosso_a/mosso-a-convocatoria-46-26/";
const BASES_URL =
    "https://portaldogc.gencat.cat/utilsEADOP/AppJava/PdfProviderServlet?documentId=1046460&type=01&language=ca_ES";
const BLOG_PATH =
    "/blog/examen-mossos-17-de-octubre-2026-plantillas-respuestas/";

const CUTOFF_HISTORY = [
    ["2023 · 46/23", "13,32", "11,65"],
    ["2023 · 46/002/23", "13,73", "12,21"],
    ["2024 · 46/24", "12,31", "Sense nota de tall"],
    ["2025 · 46/25", "Sense nota de tall", "Sense nota de tall"],
    ["2026 · 46/26", "Pendent de publicació", "Pendent de publicació"],
] as const;

const SUBTESTS = {
    knowledge: {
        title: "Coneixements",
        description: "30 preguntes · cada error descompta un quart d'encert",
        rule: MOSSOS_RULES.knowledge,
    },
    aptitude: {
        title: "Aptitudinal (psicotècnics)",
        description: "80 preguntes · els errors i els blancs no penalitzen",
        rule: MOSSOS_RULES.aptitude,
    },
    language1: {
        title: "Idioma 1",
        description: "6 preguntes · +0,25 per encert i −0,0625 per error",
        rule: MOSSOS_RULES.language,
    },
    language2: {
        title: "Idioma 2",
        description: "6 preguntes · +0,25 per encert i −0,0625 per error",
        rule: MOSSOS_RULES.language,
    },
};

type SubtestKey = keyof typeof SUBTESTS;

const emptyAnswers = (): Answers => ({ correct: "", incorrect: "" });
const initialAnswers = (): Record<SubtestKey, Answers> => ({
    knowledge: emptyAnswers(),
    aptitude: emptyAnswers(),
    language1: emptyAnswers(),
    language2: emptyAnswers(),
});

const formatScore = (value: number) =>
    value.toLocaleString("ca-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const Component = () => {
    const [answers, setAnswers] = useState(initialAnswers);
    const [languageCount, setLanguageCount] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const activeKeys: SubtestKey[] = ["knowledge", "aptitude"];
    if (languageCount >= 1) activeKeys.push("language1");
    if (languageCount === 2) activeKeys.push("language2");

    const results = activeKeys.map((key) => ({
        key,
        result: calculateSubtest(answers[key], SUBTESTS[key].rule),
    }));
    const hasErrors = results.some(({ result }) => !result.valid);
    const knowledge = results[0].result;
    const aptitude = results[1].result;
    const passed =
        knowledge.valid &&
        aptitude.valid &&
        knowledge.score >= MOSSOS_RULES.knowledge.minimum &&
        aptitude.score >= MOSSOS_RULES.aptitude.minimum;
    const total = results.reduce(
        (sum, { result }) => sum + (result.valid ? result.score : 0),
        0,
    );

    const updateAnswers = (key: SubtestKey, value: Answers) => {
        setAnswers((current) => ({ ...current, [key]: value }));
    };

    return (
        <Layout
            mode="fixed"
            seo={{
                title: "Plantilles i calculadora Mossos 46/26 | Examen 17/10/2026",
                description:
                    "Consulta les plantilles orientatives d'iOpos de l'examen de Mossos del 17 d'octubre de 2026 i calcula la nota de coneixements, aptitudinal i idiomes.",
                image: "/images/bgs/bgRank4.jpg",
            }}
        >
            <HeroSection
                filename="bgs/bgRank4"
                firstTitle="Plantilles i calculadora de nota de Mossos"
                secondTitle="Convocatòria 46/26 · 17 d'octubre de 2026"
                thirdTitle="Ens avancem amb les nostres **plantilles orientatives**, ja disponibles al blog. Consulta-les i calcula la puntuació de coneixements, psicotècnics i idiomes amb iOpos."
                alt="Estudiant davant d'un ordinador en una biblioteca"
                showScrollDownIcon={true}
            />

            <div
                lang="ca"
                className="container space-y-16 py-12 lg:space-y-20 lg:py-20"
            >
                <section className="mx-auto max-w-3xl text-center">
                    <span className="inline-flex items-center gap-2 rounded-full bg-sky-700/10 px-4 py-2 text-sm font-semibold text-sky-700">
                        <Calculator className="h-4 w-4" aria-hidden="true" />
                        Primera prova · fins a 23 punts
                    </span>
                    <h2 className="mt-5 text-balance text-3xl font-bold text-cpurple sm:text-4xl">
                        La teva nota, part per part
                    </h2>
                    <p className="mt-5 text-lg leading-relaxed text-gray-500">
                        Introdueix els encerts i els errors de cada subprova.
                        Calcularem els blancs que falten i veuràs tant la
                        puntuació total com els mínims de les parts
                        eliminatòries.
                    </p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <a
                            href="#calcula"
                            className="rounded-xl bg-glacier-700 px-5 py-3 font-semibold text-white transition-colors hover:bg-glacier-800"
                        >
                            Calcula la nota
                        </a>
                        <a
                            href="#plantilla"
                            className="rounded-xl border border-gray-200 px-5 py-3 font-semibold text-sky-700 hover:bg-sky-50"
                        >
                            Plantilles iOpos disponibles
                        </a>
                    </div>
                </section>

                <section id="calcula" className="scroll-mt-28">
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-cpurple sm:text-3xl">
                            Calculadora de la primera prova
                        </h2>
                        <p id="calculator-help" className="mt-3 text-gray-500">
                            Introdueix només els encerts i els errors amb
                            nombres enters. Els blancs es calculen
                            automàticament i no s&apos;han d&apos;editar. Els
                            camps buits compten com a zero.
                        </p>
                    </div>
                    <form
                        noValidate
                        aria-describedby="calculator-help"
                        onSubmit={(event) => {
                            event.preventDefault();
                            setSubmitted(true);
                        }}
                        onReset={() => {
                            setAnswers(initialAnswers());
                            setLanguageCount(0);
                            setSubmitted(false);
                        }}
                        className="rounded-2xl border-2 border-gray-500/10 bg-white p-5 shadow-xl sm:p-8 lg:p-10"
                    >
                        <div className="grid gap-6 lg:grid-cols-2">
                            {(["knowledge", "aptitude"] as const).map((key) => (
                                <AnswerFields
                                    key={key}
                                    id={key}
                                    {...SUBTESTS[key]}
                                    answers={answers[key]}
                                    onChange={(value) =>
                                        updateAnswers(key, value)
                                    }
                                />
                            ))}
                        </div>

                        <fieldset className="mt-8 border-t border-gray-100 pt-8">
                            <legend className="px-2 text-xl font-bold text-cpurple">
                                Idiomes · voluntària
                            </legend>
                            <p className="text-sm leading-relaxed text-gray-500">
                                Pots sumar fins a 1,5 punts per idioma i un
                                màxim de 3 entre tots dos. Aquesta part no és
                                eliminatòria.
                            </p>
                            <div className="my-5 flex flex-wrap gap-3">
                                {[0, 1, 2].map((count) => (
                                    <label
                                        key={count}
                                        className={cn(
                                            "flex cursor-pointer items-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-semibold",
                                            languageCount === count
                                                ? "border-sky-700 bg-sky-50 text-sky-700"
                                                : "border-gray-200 text-gray-600",
                                        )}
                                    >
                                        <input
                                            type="radio"
                                            name="languages"
                                            value={count}
                                            checked={languageCount === count}
                                            onChange={() =>
                                                setLanguageCount(count)
                                            }
                                            className="accent-sky-700"
                                        />
                                        {count === 0
                                            ? "Sense idiomes"
                                            : `${count} ${count === 1 ? "idioma" : "idiomes"}`}
                                    </label>
                                ))}
                            </div>
                            {languageCount === 0 ? (
                                <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">
                                    No afegeixes punts d&apos;idiomes. Pots
                                    superar la prova igualment si arribes als
                                    mínims de coneixements i aptitudinal.
                                </p>
                            ) : (
                                <div className="grid gap-6 lg:grid-cols-2">
                                    {activeKeys.slice(2).map((key) => (
                                        <AnswerFields
                                            key={key}
                                            id={key}
                                            {...SUBTESTS[key]}
                                            answers={answers[key]}
                                            onChange={(value) =>
                                                updateAnswers(key, value)
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </fieldset>

                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <button
                                type="submit"
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-glacier-700 px-6 py-4 text-lg font-bold text-white transition-colors hover:bg-glacier-800"
                            >
                                <Calculator
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                />
                                Calcula la meva nota
                            </button>
                            <button
                                type="reset"
                                className="rounded-xl border border-gray-200 px-6 py-4 font-semibold text-gray-600 hover:bg-gray-50"
                            >
                                Neteja les dades
                            </button>
                        </div>

                        <div aria-live="polite" aria-atomic="true">
                            {submitted && hasErrors && (
                                <p
                                    role="alert"
                                    className="mt-6 rounded-xl bg-red-50 p-4 font-medium text-red-700"
                                >
                                    Revisa les dades indicades a les subproves.
                                    No podem calcular la nota fins que siguin
                                    correctes.
                                </p>
                            )}
                            {submitted && !hasErrors && (
                                <div className="mt-8 rounded-2xl border border-sky-700/20 bg-sky-50 p-5 sm:p-8">
                                    <p className="text-sm font-bold uppercase tracking-widest text-sky-700">
                                        Puntuació orientativa
                                    </p>
                                    <p className="mt-3 text-6xl font-black text-cpurple sm:text-7xl">
                                        {formatScore(total)}
                                        <span className="ml-2 text-xl font-medium text-gray-500">
                                            / 23
                                        </span>
                                    </p>
                                    <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                        {results.map(({ key, result }) => (
                                            <li
                                                key={key}
                                                className="rounded-xl bg-white p-4"
                                            >
                                                <span className="block text-sm text-gray-500">
                                                    {SUBTESTS[key].title}
                                                </span>
                                                <strong className="text-lg text-cpurple">
                                                    {result.valid &&
                                                        formatScore(
                                                            result.score,
                                                        )}{" "}
                                                    /{" "}
                                                    {SUBTESTS[key].rule.maximum}
                                                </strong>
                                            </li>
                                        ))}
                                    </ul>
                                    <p
                                        className={cn(
                                            "mt-6 font-bold",
                                            passed
                                                ? "text-green-700"
                                                : "text-red-700",
                                        )}
                                    >
                                        {passed
                                            ? "Arribes als mínims de les dues subproves eliminatòries."
                                            : "No arribes als mínims de totes dues subproves eliminatòries."}
                                    </p>
                                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                                        Calen almenys 4 punts a coneixements i 5
                                        a aptitudinal. Els idiomes no compensen
                                        una subprova suspesa. Superar aquests
                                        mínims no garanteix la convocatòria a la
                                        prova física: també depèn de
                                        l&apos;ordre de puntuació i de les
                                        places previstes a les bases.
                                    </p>
                                    <section
                                        aria-labelledby="cutoffs-title"
                                        className="mt-8 border-t border-sky-700/15 pt-6"
                                    >
                                        <h3
                                            id="cutoffs-title"
                                            className="text-xl font-bold text-cpurple"
                                        >
                                            Notes de tall Mossos d&apos;Esquadra
                                        </h3>
                                        <p className="mt-2 text-sm text-gray-600">
                                            Últimes convocatòries: una
                                            referència per posar la teva nota en
                                            context.
                                        </p>
                                        <div className="mt-5 hidden overflow-x-auto rounded-2xl border border-sky-900/10 bg-white shadow-sm md:block">
                                            <table className="w-full table-fixed text-left text-sm">
                                                <caption className="sr-only">
                                                    Notes de tall de les últimes
                                                    convocatòries, en punts
                                                </caption>
                                                <thead className="bg-glacier-700 text-white">
                                                    <tr>
                                                        <th
                                                            scope="col"
                                                            className="w-[30%] px-2 py-4 text-[10px] font-semibold sm:px-5 sm:text-sm"
                                                        >
                                                            <span className="sm:hidden">
                                                                Conv.
                                                            </span>
                                                            <span className="hidden sm:inline">
                                                                Convocatòria
                                                            </span>
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className="px-2 py-4 text-[10px] font-semibold sm:px-5 sm:text-sm"
                                                        >
                                                            <span className="hidden sm:inline">
                                                                Places{" "}
                                                            </span>
                                                            no reservades
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className="px-2 py-4 text-[10px] font-semibold sm:px-5 sm:text-sm"
                                                        >
                                                            <span className="hidden sm:inline">
                                                                Places{" "}
                                                            </span>
                                                            reservades
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-sky-900/5">
                                                    {CUTOFF_HISTORY.map(
                                                        ([
                                                            call,
                                                            unreserved,
                                                            reserved,
                                                        ]) => {
                                                            const [year, code] =
                                                                call.split(
                                                                    " · ",
                                                                );
                                                            const isCurrent =
                                                                year === "2026";

                                                            return (
                                                                <tr
                                                                    key={call}
                                                                    className={cn(
                                                                        "transition-colors hover:bg-sky-50/70",
                                                                        isCurrent &&
                                                                            "bg-sky-50/70",
                                                                    )}
                                                                >
                                                                    <th
                                                                        scope="row"
                                                                        className="px-2 py-4 align-middle sm:px-5 sm:py-5"
                                                                    >
                                                                        <span className="block text-base font-bold text-slate-800 sm:text-lg">
                                                                            {
                                                                                year
                                                                            }
                                                                        </span>
                                                                        <span className="mt-1 block break-words text-[10px] font-medium text-slate-500 sm:text-xs">
                                                                            {
                                                                                code
                                                                            }
                                                                        </span>
                                                                        {isCurrent && (
                                                                            <span className="mt-2 inline-block rounded-full bg-sky-700/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-sky-800">
                                                                                Actual
                                                                            </span>
                                                                        )}
                                                                    </th>
                                                                    {[
                                                                        unreserved,
                                                                        reserved,
                                                                    ].map(
                                                                        (
                                                                            value,
                                                                            column,
                                                                        ) => (
                                                                            <td
                                                                                key={
                                                                                    column
                                                                                }
                                                                                className="px-2 py-4 align-middle sm:px-5 sm:py-5"
                                                                            >
                                                                                {value ===
                                                                                    "Sense nota de tall" ||
                                                                                value ===
                                                                                    "Pendent de publicació" ? (
                                                                                    <span
                                                                                        className={cn(
                                                                                            "inline-flex items-center gap-2 rounded-xl px-1.5 py-2 text-[10px] font-medium sm:px-2.5 sm:text-xs",
                                                                                            isCurrent
                                                                                                ? "bg-amber-100/80 text-amber-900"
                                                                                                : "bg-slate-100 text-slate-500",
                                                                                        )}
                                                                                    >
                                                                                        {isCurrent && (
                                                                                            <span
                                                                                                className="hidden h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500 sm:block"
                                                                                                aria-hidden="true"
                                                                                            />
                                                                                        )}
                                                                                        {
                                                                                            value
                                                                                        }
                                                                                    </span>
                                                                                ) : (
                                                                                    <span
                                                                                        className={cn(
                                                                                            "inline-flex flex-wrap items-center gap-1.5 rounded-xl px-1 py-2 sm:px-2.5",
                                                                                            value ===
                                                                                                "13,73"
                                                                                                ? "bg-amber-50 text-amber-800"
                                                                                                : "text-slate-800",
                                                                                        )}
                                                                                    >
                                                                                        {value ===
                                                                                            "13,73" && (
                                                                                            <Trophy
                                                                                                className="hidden h-4 w-4 shrink-0 sm:block"
                                                                                                aria-label="Nota de tall més alta"
                                                                                            />
                                                                                        )}
                                                                                        <span className="text-lg font-bold tabular-nums tracking-tight sm:text-2xl">
                                                                                            {
                                                                                                value
                                                                                            }
                                                                                        </span>
                                                                                        <span className="text-[10px] font-semibold opacity-60">
                                                                                            punts
                                                                                        </span>
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        ),
                                                                    )}
                                                                </tr>
                                                            );
                                                        },
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="mt-5 space-y-4 md:hidden">
                                            {CUTOFF_HISTORY.map(
                                                ([
                                                    call,
                                                    unreserved,
                                                    reserved,
                                                ]) => {
                                                    const isCurrent =
                                                        call === "2026 · 46/26";

                                                    return (
                                                        <article
                                                            key={call}
                                                            className={cn(
                                                                "overflow-hidden rounded-2xl border bg-white shadow-sm",
                                                                isCurrent
                                                                    ? "border-sky-700/30"
                                                                    : "border-sky-900/10",
                                                            )}
                                                        >
                                                            <div
                                                                className={cn(
                                                                    "flex flex-wrap items-center justify-between gap-2 px-4 py-3",
                                                                    isCurrent
                                                                        ? "bg-glacier-700 text-white"
                                                                        : "bg-slate-50 text-slate-800",
                                                                )}
                                                            >
                                                                <h4 className="text-sm font-bold">
                                                                    {call}
                                                                </h4>
                                                                {isCurrent && (
                                                                    <span className="rounded-full bg-white/15 px-2 py-1 text-xs font-semibold">
                                                                        Actual
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <dl className="divide-y divide-slate-100 px-4">
                                                                {[
                                                                    [
                                                                        "Places no reservades",
                                                                        unreserved,
                                                                    ],
                                                                    [
                                                                        "Places reservades",
                                                                        reserved,
                                                                    ],
                                                                ].map(
                                                                    ([
                                                                        label,
                                                                        value,
                                                                    ]) => (
                                                                        <div
                                                                            key={
                                                                                label
                                                                            }
                                                                            className="py-4"
                                                                        >
                                                                            <dt className="text-sm font-medium text-slate-500">
                                                                                {
                                                                                    label
                                                                                }
                                                                            </dt>
                                                                            <dd className="mt-2">
                                                                                {value ===
                                                                                    "Sense nota de tall" ||
                                                                                value ===
                                                                                    "Pendent de publicació" ? (
                                                                                    <span
                                                                                        className={cn(
                                                                                            "inline-block rounded-lg px-3 py-2 text-sm font-medium",
                                                                                            isCurrent
                                                                                                ? "bg-amber-50 text-amber-900"
                                                                                                : "bg-slate-100 text-slate-600",
                                                                                        )}
                                                                                    >
                                                                                        {
                                                                                            value
                                                                                        }
                                                                                    </span>
                                                                                ) : (
                                                                                    <span
                                                                                        className={cn(
                                                                                            "inline-flex flex-wrap items-center gap-2",
                                                                                            value ===
                                                                                                "13,73"
                                                                                                ? "text-amber-800"
                                                                                                : "text-slate-800",
                                                                                        )}
                                                                                    >
                                                                                        {value ===
                                                                                            "13,73" && (
                                                                                            <Trophy
                                                                                                className="h-5 w-5"
                                                                                                aria-label="Nota de tall més alta"
                                                                                            />
                                                                                        )}
                                                                                        <strong className="text-2xl tabular-nums">
                                                                                            {
                                                                                                value
                                                                                            }
                                                                                        </strong>
                                                                                        <span className="text-sm text-slate-500">
                                                                                            punts
                                                                                        </span>
                                                                                    </span>
                                                                                )}
                                                                            </dd>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </dl>
                                                        </article>
                                                    );
                                                },
                                            )}
                                        </div>
                                        <p className="mt-4 flex items-start gap-2 text-sm font-semibold text-sky-800">
                                            <Trophy
                                                className="mt-0.5 h-5 w-5 shrink-0"
                                                aria-hidden="true"
                                            />
                                            La nota de tall més alta
                                            d&apos;aquestes convocatòries és de
                                            13,73 punts (46/002/23, places no
                                            reservades).
                                        </p>
                                        <p className="mt-3 text-xs leading-relaxed text-gray-500">
                                            Aquestes dades són històriques i no
                                            equivalen als mínims per superar les
                                            subproves. La nota de tall de la
                                            46/26 està pendent de publicació:
                                            superar un tall anterior no
                                            garanteix passar a la prova física.
                                            «Sense nota de tall» no vol dir que
                                            es pugui suspendre una subprova.
                                        </p>
                                    </section>
                                </div>
                            )}
                        </div>
                    </form>
                </section>

                <section
                    id="plantilla"
                    className="scroll-mt-28 rounded-2xl border border-amber-200 bg-amber-50 p-6 sm:p-8"
                >
                    <div className="flex items-center gap-3 text-amber-800">
                        <FileText
                            className="h-6 w-6 shrink-0"
                            aria-hidden="true"
                        />
                        <h2 className="text-xl font-bold sm:text-2xl">
                            Les plantilles d&apos;iOpos ja són al blog
                        </h2>
                    </div>
                    <p className="mt-4 font-semibold text-amber-900">
                        Encara no s&apos;ha publicat la plantilla oficial.
                    </p>
                    <p className="mt-3 leading-relaxed text-amber-900/80">
                        A iOpos ens avancem a la publicació oficial: ja hem
                        publicat al blog les nostres propostes de respostes de
                        l&apos;examen del 17 d&apos;octubre, elaborades a partir
                        de diferents criteris d&apos;anàlisi i estadístiques.
                        Fes-les servir com a referència per comptar els encerts,
                        els errors i els blancs, i introdueix el recompte a la
                        calculadora.
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-amber-900/80">
                        Són plantilles pròpies i orientatives, no oficials ni
                        definitives. Poden diferir de les que publiqui el
                        tribunal; revisa la teva estimació quan es coneguin les
                        respostes oficials.
                    </p>
                    <Link
                        to={BLOG_PATH}
                        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-glacier-700 px-5 py-3 font-semibold text-white transition-colors hover:bg-glacier-800"
                    >
                        Veure les plantilles orientatives d&apos;iOpos
                        <FileText className="h-4 w-4" aria-hidden="true" />
                    </Link>
                    <a
                        href={OFFICIAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-5 flex w-fit items-center gap-2 font-semibold text-amber-900 underline"
                    >
                        Consulta les publicacions del tribunal
                        <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                </section>

                <section aria-labelledby="scoring-title">
                    <h2
                        id="scoring-title"
                        className="text-2xl font-bold text-cpurple sm:text-3xl"
                    >
                        Quins barems aplica la calculadora?
                    </h2>
                    <p className="mt-3 text-gray-500">
                        Resum de la primera prova de la convocatòria 46/26. Els
                        blancs no penalitzen i cap subprova dona una puntuació
                        negativa.
                    </p>
                    <div className="mt-8 grid gap-5 lg:grid-cols-3">
                        {[
                            {
                                title: "Coneixements",
                                badge: "Eliminatòria · mínim 4",
                                text: "30 preguntes i 35 minuts. Els errors resten una quarta part del valor d'un encert.",
                                formula:
                                    "Nota = (encerts − errors / 4) × 10 / 30",
                                range: "De 0 a 10 punts · base 6.1.1.a",
                            },
                            {
                                title: "Aptitudinal",
                                badge: "Eliminatòria · mínim 5",
                                text: "80 preguntes i 35 minuts. Només sumen els encerts; els errors no descompten.",
                                formula: "Nota = encerts × 10 / 80",
                                range: "De 0 a 10 punts · base 6.1.1.b",
                            },
                            {
                                title: "Idiomes",
                                badge: "Voluntària · no elimina",
                                text: "6 preguntes per idioma, fins a dos idiomes. Cada encert suma 0,25 i cada error resta 0,0625.",
                                formula:
                                    "Per idioma = encerts × 0,25 − errors × 0,0625",
                                range: "De 0 a 1,5 per idioma · base 6.1.1.c",
                            },
                        ].map((item) => (
                            <article
                                key={item.title}
                                className="rounded-2xl border border-gray-200 p-6"
                            >
                                <CheckCircle2
                                    className="mb-4 h-6 w-6 text-sky-700"
                                    aria-hidden="true"
                                />
                                <h3 className="text-xl font-bold text-cpurple">
                                    {item.title}
                                </h3>
                                <p className="mt-2 text-sm font-semibold text-sky-700">
                                    {item.badge}
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-gray-500">
                                    {item.text}
                                </p>
                                <p className="mt-4 rounded-lg bg-gray-50 p-3 text-sm font-semibold text-gray-700">
                                    {item.formula}
                                </p>
                                <p className="mt-4 text-xs text-gray-500">
                                    {item.range}
                                </p>
                            </article>
                        ))}
                    </div>
                    <p className="mt-6 text-sm leading-relaxed text-gray-500">
                        Font de referència:{" "}
                        <a
                            href={BASES_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-sky-700 underline"
                        >
                            Resolució ISP/1763/2026, de 3 de juny (DOGC núm.
                            9681)
                        </a>
                        . El total es calcula sense arrodoniments intermedis;
                        les puntuacions es mostren amb dos decimals.
                    </p>
                </section>

                <section className="rounded-2xl bg-gray-50 p-6 sm:p-8">
                    <h2 className="text-2xl font-bold text-cpurple">
                        El resultat oficial el determina el tribunal
                    </h2>
                    <p className="mt-4 leading-relaxed text-gray-600">
                        Una plantilla provisional pot canviar arran de les
                        al·legacions. Si es rectifica una resposta o
                        s&apos;anul·la alguna pregunta, cal revisar el recompte
                        i els criteris de correcció. Aquesta eina no aplica
                        anul·lacions ni preguntes de reserva automàticament.
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-gray-500">
                        iOpos és una acadèmia privada, independent de la
                        Generalitat de Catalunya. El càlcul és orientatiu: les
                        bases, els acords i els resultats oficials del tribunal
                        sempre prevalen.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-5 text-sm font-semibold text-sky-700">
                        <a
                            href={OFFICIAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                        >
                            Convocatòria oficial 46/26
                        </a>
                        <Link to="/calculadora-iopos/" className="underline">
                            Calculadora general iOpos
                        </Link>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

function AnswerFields({
    id,
    title,
    description,
    rule,
    answers,
    onChange,
}: {
    id: string;
    title: string;
    description: string;
    rule: SubtestRule;
    answers: Answers;
    onChange: (answers: Answers) => void;
}) {
    const result = calculateSubtest(answers, rule);
    const invalid = !result.valid;

    return (
        <fieldset
            className={cn(
                "min-w-0 rounded-xl border p-4 sm:p-5",
                invalid ? "border-red-500 bg-red-50" : "border-gray-200",
            )}
            aria-describedby={`${id}-help${invalid ? ` ${id}-error` : ""}`}
        >
            <legend
                className={cn(
                    "px-2 text-lg font-bold",
                    invalid ? "text-red-700" : "text-cpurple",
                )}
            >
                {title}
            </legend>
            <p
                id={`${id}-help`}
                className="mb-5 text-sm leading-relaxed text-gray-500"
            >
                {description}
            </p>
            <div className="grid grid-cols-3 gap-3">
                {(
                    [
                        ["correct", "Encerts"],
                        ["incorrect", "Errors"],
                        ["blank", "En blanc"],
                    ] as const
                ).map(([key, label]) => (
                    <div key={key} className="min-w-0">
                        <label
                            htmlFor={`${id}-${key}`}
                            className={cn(
                                "mb-2 block text-sm font-semibold",
                                invalid ? "text-red-700" : "text-gray-700",
                            )}
                        >
                            {label}
                        </label>
                        <input
                            id={`${id}-${key}`}
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={rule.questions}
                            step={1}
                            readOnly={key === "blank"}
                            value={
                                key === "blank"
                                    ? result.valid
                                        ? result.blank
                                        : ""
                                    : answers[key]
                            }
                            placeholder={key === "blank" ? "—" : "0"}
                            onChange={
                                key === "blank"
                                    ? undefined
                                    : (event) =>
                                          onChange({
                                              ...answers,
                                              [key]: event.target.value,
                                          })
                            }
                            aria-invalid={invalid}
                            aria-describedby={
                                invalid ? `${id}-error` : `${id}-help`
                            }
                            className={cn(
                                "h-14 w-full min-w-0 rounded-xl border-2 px-2 text-center text-lg font-bold",
                                invalid
                                    ? "border-red-500 text-red-700 placeholder:text-red-400 focus-visible:outline-red-700"
                                    : "border-gray-200 focus-visible:outline-sky-700",
                                key === "blank" && "bg-gray-50",
                            )}
                        />
                    </div>
                ))}
            </div>
            {invalid && !result.valid && (
                <p
                    id={`${id}-error`}
                    role="alert"
                    className="mt-3 text-sm font-medium text-red-700"
                >
                    {result.error}
                </p>
            )}
        </fieldset>
    );
}

export default Component;
